import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";
import { downloadAdminFile, uploadAdminFile } from "../../app/files";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminSession, useAdminUiStore } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen } from "../../app/routes";
import { saveBlob } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import { StateNotice } from "../workspace/shared";
import { ContentView } from "./components/ContentView";
import { useContentDetail, useContentMutation } from "./queries";
import type { ContentMutation } from "./queries";
import { parseContentType } from "./schema";

function detailState(query: ReturnType<typeof useContentDetail>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function ContentDetailPageForId({ contentId }: { readonly contentId: number }) {
  const services = useAdminApplication();
  const session = useAdminSession();
  const setFlashNotice = useAdminUiStore((state) => state.setFlashNotice);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useContentDetail(contentId);
  const mutation = useContentMutation();
  const editing = searchParams.get("mode") === "edit";
  const navigation = useAdminNavigation({
    contextKey: "contents",
    defaultListPath: `/contents?type=${query.data ? parseContentType(query.data.contentType) : "NOTICE"}`,
    originLabel: "콘텐츠 상세",
  });
  const [notice, setNotice] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const fileRequests = useRef(new Set<AbortController>());

  useEffect(() => {
    const canonical = new URLSearchParams();
    if (editing) canonical.set("mode", "edit");
    if (canonical.toString() !== searchParams.toString()) setSearchParams(canonical, { replace: true, state: location.state });
  }, [editing, location.state, searchParams, setSearchParams]);

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const complete = async (input: ContentMutation, result: unknown) => {
    setRecoveryError("");
    if (input.kind === "delete") {
      setFlashNotice("contents", `콘텐츠 ID ${input.id}를 삭제했습니다.`);
      navigation.backToList();
      return;
    }
    const saved = result as Schemas["ContentPostView"];
    if (input.kind === "update") setSearchParams(new URLSearchParams(), { replace: true, state: location.state });
    setNotice(input.kind === "publish"
      ? `${saved.title}을 게시했습니다. 서버 상태: ${saved.status}`
      : input.kind === "archive"
        ? `${saved.title}을 보관했습니다. 서버 상태: ${saved.status}`
        : `${saved.title} · 서버 ID ${saved.id}를 임시 저장했습니다. 자동 게시되지 않았습니다.`);
    const refreshed = await query.refetch();
    if (refreshed.error) setRecoveryError("처리 응답은 받았지만 최신 상세를 불러오지 못했습니다. 다시 조회해 주세요.");
  };

  const upload = async (file: File) => {
    const controller = new AbortController();
    fileRequests.current.add(controller);
    try { return await uploadAdminFile(services, file, "CONTENT", controller.signal); }
    finally { fileRequests.current.delete(controller); }
  };

  const download = useCallback(async (fileAssetId: number) => {
    const controller = new AbortController();
    fileRequests.current.add(controller);
    try {
      const blob = await downloadAdminFile(services, fileAssetId, { businessRefType: "CONTENT", businessRefId: contentId }, controller.signal);
      saveBlob(blob, `content-${contentId}-attachment-${fileAssetId}`);
    } finally { fileRequests.current.delete(controller); }
  }, [contentId, services]);

  const type = query.data ? parseContentType(query.data.contentType) : "NOTICE";
  return <ContentView
    mode="detail"
    editing={editing}
    type={type}
    status=""
    selectedId={contentId}
    detail={query.data}
    detailState={detailState(query)}
    canManage={session.authSession?.authorities?.includes("CONTENT_MANAGE") ?? false}
    notice={notice}
    recoveryError={recoveryError}
    detailHref={(id) => pathForAdminEntityScreen("27", id)}
    backHref={navigation.backPath}
    backLabel={navigation.backLabel}
    onBack={navigation.backToList}
    onStartEdit={() => setSearchParams(new URLSearchParams({ mode: "edit" }), { replace: true, state: location.state })}
    onCancelEdit={() => setSearchParams(new URLSearchParams(), { replace: true, state: location.state })}
    onTypeChange={() => undefined}
    onStatusChange={() => undefined}
    onSelect={() => undefined}
    onRetryList={() => undefined}
    onRetryDetail={() => { void query.refetch(); }}
    onMutation={(input) => mutation.mutateAsync(input)}
    onMutationComplete={complete}
    onUpload={upload}
    onDownload={download}
  />;
}

export function ContentDetailPage() {
  const contentId = parseAdminEntityId(useParams().contentId);
  if (contentId === null) return <StateNotice state="not-found" />;
  return <ContentDetailPageForId key={contentId} contentId={contentId} />;
}
